import { UserInfo } from './interface'
import { DynamoDB } from '@/provider/dynamodb'
import AWSConfig from '@/assets/aws-exports'

export type UserInfoKey = {
  division: string;
  sortDate: string;
}

export interface LastEvaluatedKey {
  division: string;
  sortDate: string;
}

export interface UserInfoResult {
  data: Array<UserInfo>;
  lastEvaluatedKey?: LastEvaluatedKey;
}

export interface UserInfoProvider {
  getDynamoDB (): DynamoDB,
  getTableName (): string,
  setDivision (value: string): void
  getDivision (): string
  updateValue (key: UserInfoKey, attributeName: string, attributeValue: any): Promise<void>
  updateValues (key: UserInfoKey, ...attributes: Array<{name: string, value: any}>): Promise<void>
  loadByUserId (userId: string): Promise<UserInfo>
  loadByUsername (username: string): Promise<UserInfoResult>
  loadByEmail (email: string): Promise<UserInfoResult>
  loadByCrcId (limit: number, crcId: string, initialLastEvaluatedKey?: LastEvaluatedKey): Promise<UserInfoResult>
  loadBySortDateRange (limit: number, sortDateStart: Date, sortDateEnd: Date, initialLastEvaluatedKey?: LastEvaluatedKey): Promise<UserInfoResult>
  load (limit: number, initialLastEvaluatedKey?: LastEvaluatedKey): Promise<UserInfoResult>
  loadUpdatedAt (): Promise<Array<Date>>
}

export class ManagerUserInfoProvider implements UserInfoProvider {
  constructor (protected db: DynamoDB, protected tableName: string, protected division: string = '') {
  }

  getDynamoDB () {
    return this.db
  }

  getTableName () {
    return this.tableName
  }

  setDivision (value: string) {
    this.division = value
  }

  getDivision () {
    return this.division
  }

  async updateValue (key: UserInfoKey, attributeName: string, attributeValue: any) {
    const params = {
      TableName: this.tableName,
      Key: {
        division: key.division,
        sortDate: key.sortDate
      },
      UpdateExpression: 'set #attr = :value',
      ExpressionAttributeNames: {
        '#attr': attributeName
      },
      ExpressionAttributeValues: {
        ':value': attributeValue
      }
    }
    await this.db.update(params)
  }

  async updateValues (key: UserInfoKey, ...attributes: Array<{name: string, value: any}>) {
    const params = {
      TableName: this.tableName,
      Key: {
        division: key.division,
        sortDate: key.sortDate
      },
      UpdateExpression: 'set ',
      ExpressionAttributeNames: {} as any,
      ExpressionAttributeValues: {} as any
    }
    attributes.forEach((e, i) => {
      if (i > 0) {
        params.UpdateExpression += ', ' 
      }
      const name = `#attr${i}`
      const value = `:value${i}`
      params.UpdateExpression += `${name} = ${value}`
      params.ExpressionAttributeNames[name] = e.name
      params.ExpressionAttributeValues[value] = e.value
    })
    console.log("updateValues params: ", params)
    await this.db.update(params)
  }

  async loadByUserId (userId: string): Promise<UserInfo> {
    const params = this.getQueryUserIdParams(userId)
    const userInfoList = (await this.db.query(params)).Items as UserInfo[]
    console.debug('loadByUserId ', userInfoList)
    if (!userInfoList || userInfoList.length === 0) {
      throw Error(`Not Found UserInfo by userId = ${userId}`)
    }
    if (userInfoList.length > 1) {
      // TODO 要削除
      // 割付前のデータが残ってしまうバグに対処するため
      // バグが解消され次第削除
      const filteredUserInfoList = userInfoList.filter(e => e.appStartedAt !== undefined) as UserInfo[]
      if (!filteredUserInfoList || filteredUserInfoList.length === 0) {
        throw Error(`Not Found UserInfo by userId = ${userId}`)
      }
      if (filteredUserInfoList.length > 1) {
        throw Error('MultipulUserHitError')
      }
      return filteredUserInfoList[0]
    }
    return userInfoList[0]
  }

  async loadByUsername (username: string) {
    const params = this.getQueryUsernameParams(username)
    const result = await this.querySingleUserInfo(params)
    return result
  }

  async loadByEmail (email: string) {
    const params = this.getQueryEmailParams(email)
    const result = await this.querySingleUserInfo(params)
    return result
  }

  async loadByCrcId (limit: number, crcId: string, initialLastEvaluatedKey?: LastEvaluatedKey) {
    let lastEvaluatedKey = initialLastEvaluatedKey
    let userInfoItems: Array<UserInfo> = []

    while (userInfoItems.length < limit) {
      const params = this.getQueryCrcIdParams(limit, crcId, lastEvaluatedKey)
      const result: UserInfoResult = await this.queryMultiUserInfo(params)
      userInfoItems = userInfoItems.concat(result.data)
      lastEvaluatedKey = result.lastEvaluatedKey
      if (!lastEvaluatedKey) {
        break
      }
    }
    return { data: userInfoItems, lastEvaluatedKey: lastEvaluatedKey }
  }

  async loadBySortDateRange (limit:number, sortDateStart: Date, sortDateEnd: Date, initialLastEvaluatedKey?: LastEvaluatedKey) {
    let lastEvaluatedKey = initialLastEvaluatedKey
    let userInfoItems: Array<UserInfo> = []
    let requestItemCount = limit
    const start = sortDateStart.toISOString()
    const end = sortDateEnd.toISOString()
    while (userInfoItems.length < limit) {
      const result: UserInfoResult = await this.singleKeyRangeLoad(start, end, limit, lastEvaluatedKey)
      userInfoItems = userInfoItems.concat(result.data)
      requestItemCount = requestItemCount - result.data.length
      lastEvaluatedKey = result.lastEvaluatedKey
      if (!lastEvaluatedKey) {
        break
      }
    }
    return { data: userInfoItems, lastEvaluatedKey: lastEvaluatedKey }
  }

  /**
   * DBよりUserInfoのデータをlimit分取得します。 initialLastEvaluatedKeyが指定されていた場合そのキーからデータを取得し始めます。
   * 取得していないデータが残っている場合はlastEvaluatedKeyに次の取得開始するキーが入ります。それ以上取得できるデータがない場合はlastEvaluatedKeyにはundefinedが入ります。
   *
   * @name load
   * @function
   * @param {number} limit
   * @param {{division} initialLastEvaluatedKey?
   * @param {string}} sortDate
   * @returns {Promise<UserInfoResult>}
   */
  async load (limit: number, initialLastEvaluatedKey?: LastEvaluatedKey): Promise<UserInfoResult> {
    let userInfoItems: Array<UserInfo> = []
    let lastEvaluatedKey = initialLastEvaluatedKey
    let requestItemCount = limit
    while (userInfoItems.length < limit) {
      const result: UserInfoResult = await this.singleKeyLoad(requestItemCount, lastEvaluatedKey)
      userInfoItems = userInfoItems.concat(result.data)
      requestItemCount = requestItemCount - result.data.length
      lastEvaluatedKey = result.lastEvaluatedKey
      if (!lastEvaluatedKey) {
        break
      }
    }
    // limitちょうどで終わった場合、次の検索開始地点が失われるため、最後の要素をlastEvaluatedKeyとして与える。
    // 次の検索はここで与えたlastEvaluatedKeyから始まる。
    // そのため一回リクエストが多く発生してしまう
    if (!lastEvaluatedKey && userInfoItems.length > 0) {
      lastEvaluatedKey = {
        division: userInfoItems[userInfoItems.length - 1].division,
        sortDate: userInfoItems[userInfoItems.length - 1].sortDate
      }
    }
    return { data: userInfoItems, lastEvaluatedKey: lastEvaluatedKey }
  }

  protected processUserInfo (userInfo: UserInfo): UserInfo {
    if (userInfo.weeklyAccessLog === undefined) {
      userInfo['weeklyAccessTime'] = 0
      return userInfo
    }
    let totalAccessTime:number = 0
    let pauseDate: Date | undefined
    for (let i = 0; i < userInfo.weeklyAccessLog.length; i++) {
      // データが1週間前以上の場合扱わない
      if (new Date(userInfo.weeklyAccessLog[i].createdAt).getTime() < new Date().getTime() - 1000 * 3600 * 24 * 7) {
        continue
      }
      if (userInfo.weeklyAccessLog[i].status === 'resume' && pauseDate !== undefined) {
        totalAccessTime += pauseDate.getTime() - new Date(userInfo.weeklyAccessLog[i].createdAt).getTime()
        pauseDate = undefined
        continue
      } else if (userInfo.weeklyAccessLog[i].status === 'pause') {
        pauseDate = new Date(userInfo.weeklyAccessLog[i].createdAt)
      }
    }
    userInfo['weeklyAccessTime'] = Math.floor(totalAccessTime / 1000 / 60)
    return userInfo
  }

  protected async querySingleUserInfo (params:any) {
    const userInfo = await this.db.query(params)
    if (!userInfo.Items) {
      throw Error('UnknownError')
    }
    if (userInfo.Items.length === 0) {
      return { data: [] }
    }
    if (userInfo.Items.length > 1) {
      // TODO 要削除
      // 割付前のデータが残ってしまうバグに対処するため
      // バグが解消され次第削除
      return { data: userInfo.Items.filter(e => {
        return e.appStartedAt !== undefined
      }) as Array<UserInfo> }
      // throw Error('MultipulUserHitError')
    }
    const processedUserInfo = userInfo.Items.map((e: any) => {
      return this.processUserInfo(e)
    })
    return { data: processedUserInfo }
  }

  protected async queryMultiUserInfo (params: any): Promise<UserInfoResult> {
    const userInfo = await this.db.query(params)
    if (!userInfo.Items) {
      return { data: [] }
    }
    const processedUserInfo = userInfo.Items.map((e: any) => {
      return this.processUserInfo(e)
    })
    return { data: processedUserInfo, lastEvaluatedKey: userInfo.LastEvaluatedKey as any }
  }

  protected async singleKeyLoad (
    limit: number,
    lastEvaluatedKey?: LastEvaluatedKey
  ): Promise<UserInfoResult> {
    if (!this.division) {
      throw new TypeError('division property is needed')
    }

    const params = this.getQuerySortKeyParams(limit, lastEvaluatedKey)
    const userInfo = await this.db.query(params)
    if (!userInfo.Items) {
      return { data: [] }
    }
    const processedUserInfo = userInfo.Items.map((e: any) => {
      return this.processUserInfo(e)
    })
    return { data: processedUserInfo, lastEvaluatedKey: userInfo.LastEvaluatedKey as any }
  }

  protected async singleKeyRangeLoad (
    start:string,
    end:string,
    limit: number,
    lastEvaluatedKey?: LastEvaluatedKey
  ): Promise<UserInfoResult> {
    if (!this.division) {
      throw new TypeError('division property is needed')
    }

    const params = this.getQuerySortKeyRangeParams(start, end, limit, lastEvaluatedKey)
    const userInfo = await this.db.query(params)
    if (!userInfo.Items) {
      return { data: [] }
    }
    const processedUserInfo = userInfo.Items.map((e: any) => {
      return this.processUserInfo(e)
    })
    return { data: processedUserInfo, lastEvaluatedKey: userInfo.LastEvaluatedKey as any }
  }

  async loadUpdatedAt () {
    const params = {
      TableName: AWSConfig.userInfoUpdatedDateTable
    }
    const userInfoUpdatedDate = await this.db.scan(params)
    if (!userInfoUpdatedDate.Items) {
      throw Error('Cannot load userinfo updated date.')
    }
    return userInfoUpdatedDate.Items as Array<Date>
  }

  /**
   * ユーザー情報を取得するためのクエリパラメータを返します。
   * @param {string} userId
   * @returns {any}
   */
  protected getQueryUserIdParams (userId: string) {
    return {
      TableName: this.tableName,
      IndexName: 'userId-index',
      KeyConditionExpression: 'userId = :userId',
      ExpressionAttributeValues: {
        ':userId': userId
      }
    } as any
  }

  /**
   * ユーザー情報を取得するためのクエリパラメータを返します。
   * @param {string} username
   * @returns {any}
   */
  protected getQueryUsernameParams (username: string) {
    return {
      TableName: this.tableName,
      IndexName: 'username-index',
      KeyConditionExpression: 'username = :username',
      ExpressionAttributeValues: {
        ':username': username
      }
    } as any
  }

  /**
   * ユーザー情報を取得するためのクエリパラメータを返します。
   * @param {string} email
   * @returns {any}
   */
  protected getQueryEmailParams (email: string) {
    return {
      TableName: this.tableName,
      IndexName: 'email-index',
      KeyConditionExpression: 'email = :email',
      ExpressionAttributeValues: {
        ':email': email
      }
    } as any
  }

  /**
   * ユーザー情報を取得するためのクエリパラメータを返します。
   * @param limit 
   * @param crcId 
   * @param lastEvaluatedKey 
   * @returns {any}
   */
  protected getQueryCrcIdParams (limit: number, crcId: string, lastEvaluatedKey?: LastEvaluatedKey) {
    return {
      TableName: this.tableName,
      IndexName: 'crcId-index',
      KeyConditionExpression: 'crcId = :crcId',
      FilterExpression: 'division = :division',
      ExpressionAttributeValues: {
        ':crcId': crcId,
        ':division': this.division
      },
      ExclusiveStartKey: lastEvaluatedKey,
      Limit: limit,
      ScanIndexForward: false
    } as any
  }

  /**
   * ユーザー情報を取得するためのクエリパラメータを返します。
   * @param limit
   * @param lastEvaluatedKey
   * @returns {any}
   */
  protected getQuerySortKeyParams (limit: number, lastEvaluatedKey?: LastEvaluatedKey) {
    return {
      TableName: this.tableName,
      KeyConditionExpression: 'division = :division',
      ExpressionAttributeValues: {
        ':division': this.division
      },
      ExclusiveStartKey: lastEvaluatedKey,
      Limit: limit,
      ScanIndexForward: false
    } as any
  }

  /**
   * ユーザー情報を取得するためのクエリパラメータを返します。
   * @param start
   * @param end
   * @param limit
   * @param lastEvaluatedKey
   * @returns {any}
   */
  protected getQuerySortKeyRangeParams (start:string, end:string, limit: number, lastEvaluatedKey?: LastEvaluatedKey) {
    return {
      TableName: this.tableName,
      KeyConditionExpression: 'division = :division and sortDate between :start and :end',
      ExpressionAttributeValues: {
        ':division': this.division,
        ':start': start,
        ':end': end
      },
      ExclusiveStartKey: lastEvaluatedKey,
      Limit: limit,
      ScanIndexForward: false
    } as any
  }
}

export class RewardStatusUserInfoProvider extends ManagerUserInfoProvider {
  constructor (db: DynamoDB, tableName: string, division: string, private rewardStatus: string) {
    super(db, tableName, division)
  }

  setRewardStatus (value: string) {
    this.rewardStatus = value
  }

  getRewardStatus () {
    return this.rewardStatus
  }

  private setRewardStatusFilterParams (params: any) {
    params.FilterExpression = 'rewardStatus = :rewardStatus'
    params.ExpressionAttributeValues[':rewardStatus'] = this.rewardStatus
    return params
  }

  private addRewardStatusFilterParams (params: any) {
    params.FilterExpression = params.FilterExpression + ' AND rewardStatus = :rewardStatus'
    params.ExpressionAttributeValues[':rewardStatus'] = this.rewardStatus
    return params
  }

  protected getQueryUserIdParams (userId: string) {
    return this.setRewardStatusFilterParams(super.getQueryUserIdParams(userId))
  }

  protected getQueryUsernameParams (username: string) {
    return this.setRewardStatusFilterParams(super.getQueryUsernameParams(username))
  }

  protected getQueryEmailParams (email: string) {
    return this.setRewardStatusFilterParams(super.getQueryEmailParams(email))
  }

  protected getQueryCrcIdParams (limit: number, crcId: string, lastEvaluatedKey?: LastEvaluatedKey) {
    return this.addRewardStatusFilterParams(super.getQueryCrcIdParams(limit, crcId, lastEvaluatedKey))
  }

  protected getQuerySortKeyParams (limit: number, lastEvaluatedKey?: LastEvaluatedKey) {
    return this.setRewardStatusFilterParams(super.getQuerySortKeyParams(limit, lastEvaluatedKey))
  }

  protected getQuerySortKeyRangeParams (start:string, end:string, limit: number, lastEvaluatedKey?: LastEvaluatedKey) {
    return this.setRewardStatusFilterParams(super.getQuerySortKeyRangeParams(start, end, limit, lastEvaluatedKey))
  }
}
